import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  PhoneCall,
  PhoneMissed,
  RefreshCw,
  Timer,
} from 'lucide-react';
import { telephonyApi } from '../../telephony/telephonyApi';
import type { CallStatistics } from '../../telephony/types';

function seconds(value: number) {
  const safe = Math.max(0, Math.round(Number(value || 0)));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function totalTime(value: number) {
  const minutes = Math.round(Math.max(0, Number(value || 0)) / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${minutes} мин`;
  return `${hours} ч ${rest} мин`;
}

function percent(value: number) {
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value)}%`;
}

function shortDate(value: string) {
  const date = new Date(`${value}T00:00:00+03:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(date);
}

export function CallStatisticsView({ data }: { data: CallStatistics }) {
  const maxCalls = Math.max(1, ...data.trend.map(point => point.totalCalls));
  const metrics = [
    { label: 'Всего звонков', value: data.summary.totalCalls, note: `${data.periodDays} дней`, icon: PhoneCall },
    { label: 'Входящие', value: data.summary.inboundCalls, note: 'Приняты порталом', icon: ArrowDownLeft },
    { label: 'Исходящие', value: data.summary.outboundCalls, note: 'Начаты сотрудниками', icon: ArrowUpRight },
    { label: 'Отвеченные', value: data.summary.answeredCalls, note: percent(data.summary.answerRate), icon: CheckCircle2 },
    { label: 'Пропущенные', value: data.summary.missedCalls, note: `${data.summary.failedCalls} с ошибкой`, icon: PhoneMissed },
    { label: 'Среднее ожидание', value: seconds(data.summary.averageWaitSeconds), note: `разговор ${seconds(data.summary.averageDurationSeconds)}`, icon: Clock3 },
  ];

  return <div className="calls-statistics">
    <div className="calls-statistics__scope">
      <span>{data.scope === 'TEAM' ? 'Вся команда' : 'Только ваши звонки'}</span>
      <b><Timer /> {totalTime(data.summary.totalDurationSeconds)} разговоров</b>
    </div>
    <div className="calls-statistics__metrics">
      {metrics.map(metric => {
        const Icon = metric.icon;
        return <article className="surface" key={metric.label}><span><Icon /></span><div><small>{metric.label}</small><strong>{metric.value}</strong><em>{metric.note}</em></div></article>;
      })}
    </div>

    <section className="surface calls-statistics__trend">
      <header><div><h2>Динамика по дням</h2><p>Ответы и пропущенные вызовы за выбранный период</p></div></header>
      {data.trend.length > 0 ? <div className="calls-statistics__bars" aria-label="Динамика звонков">
        {data.trend.map(point => <div key={point.bucket} title={`${shortDate(point.bucket)}: ${point.totalCalls}`}>
          <span className="calls-statistics__bar-stack" style={{ height: `${Math.max(10, point.totalCalls / maxCalls * 150)}px` }}>
            <i className="answered" style={{ flexGrow: point.answeredCalls }} aria-label={`Отвечено: ${point.answeredCalls}`} />
            <i className="missed" style={{ flexGrow: point.missedCalls }} aria-label={`Пропущено: ${point.missedCalls}`} />
          </span>
          <b>{point.totalCalls}</b><small>{shortDate(point.bucket)}</small>
        </div>)}
      </div> : <div className="calls-statistics__empty">За выбранный период звонков нет.</div>}
    </section>

    {data.scope === 'TEAM' && <section className="surface calls-statistics__operators">
      <header><div><h2>Показатели сотрудников</h2><p>Сводка рассчитана сервером по фактическим участникам звонков</p></div></header>
      <div className="calls-statistics__table">
        <div><span>Сотрудник</span><span>Всего</span><span>Отвечено</span><span>Пропущено</span><span>Доля ответов</span><span>Средний разговор</span></div>
        {data.operators.map(operator => <article key={operator.userId}>
          <b>{operator.fullName}</b><span>{operator.totalCalls}</span><span>{operator.answeredCalls}</span><span>{operator.missedCalls}</span><span>{percent(operator.answerRate)}</span><span>{seconds(operator.averageDurationSeconds)}</span>
        </article>)}
      </div>
      {!data.operators.length && <div className="calls-statistics__empty">Нет данных по сотрудникам.</div>}
    </section>}
  </div>;
}

export function CallStatisticsPanel() {
  const [periodDays, setPeriodDays] = useState<7 | 30 | 90>(30);
  const query = useQuery({
    queryKey: ['telephony-call-statistics', periodDays],
    queryFn: () => telephonyApi.callStatistics(periodDays),
    staleTime: 30_000,
  });

  return <div className="calls-statistics-panel">
    <div className="calls-statistics-panel__toolbar">
      <div>{([7, 30, 90] as const).map(days => <button type="button" key={days} className={periodDays === days ? 'active' : ''} onClick={() => setPeriodDays(days)}>{days} дней</button>)}</div>
      <button type="button" className="button ghost compact" onClick={() => void query.refetch()} disabled={query.isFetching}><RefreshCw className={query.isFetching ? 'spinning' : ''} /> Обновить</button>
    </div>
    {query.isLoading && <div className="calls-page__loading surface"><PhoneCall /><span><b>Загружаем статистику звонков</b><small>Собираем показатели за выбранный период.</small></span></div>}
    {query.isError && <div className="calls-statistics__error surface"><b>Не удалось загрузить статистику</b><small>{query.error instanceof Error ? query.error.message : 'Повторите попытку.'}</small><button type="button" className="button ghost compact" onClick={() => void query.refetch()}>Повторить</button></div>}
    {query.data && <CallStatisticsView data={query.data} />}
  </div>;
}
